import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  Inject,
  Injector,
  OnDestroy,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';

import { CommonConstants } from '../../../../../shared';
import {
  CitationService,
  ContextMenuEditorService,
} from '../../../../../services';
import { takeUntil } from 'rxjs/operators';
import { ContextMenuInterface } from '../../../../../model';
import { WkDecoratorActionService } from '../../../../../modules/wk-document-decorator/services';
import { AppStore } from '../../../../../store';
import {
  loadAIUMCReleaseNotes,
  setProcessingStateOnUMCAPI,
  setTriggerSaveApiInDoc,
} from '../../../../../modules/ai-umc';
import { setSelectedCommentsTabIndex } from '../../../../../modules/wk-document-decorator/state';
import { SidePanelTabs } from '../../../../../enums';

@Component({
  selector: 'wk-context-menu-editor',
  templateUrl: './wk-context-menu-editor.component.html',
  styleUrls: ['./wk-context-menu-editor.component.scss'],
  standalone: false,
})
export class WKContextMenuComponent implements OnInit, OnDestroy {
  @Input()
  contextMenuObject: any;
  clientX: number;
  public showSubMenu = false;
  private rightClickEventDetails: any;
  private globalTop: number;
  private globalLeft: number;
  public flagForCoords = false;
  public userClickedHeight: number;
  private readonly _onDestroy$ = new Subject<void>();
  private readonly wkDecoratorActionsService: WkDecoratorActionService;
  private readonly store: Store<AppStore>;
  protected readonly largeOffset: number = 50;
  protected readonly smallOffset: number = 100;

  constructor(
    private readonly eRef: ElementRef,
    protected readonly contextmenuService: ContextMenuEditorService,
    private readonly citationService: CitationService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly injector: Injector
  ) {
    this.wkDecoratorActionsService =
      this.injector.get<WkDecoratorActionService>(WkDecoratorActionService);
    this.store = this.injector.get<Store<AppStore>>(Store);
  }

  ngOnInit() {
    this.citationService.setRightClickEventDetails
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((data) => {
        this.rightClickEventDetails = data;
      });
    this.wkDecoratorActionsService.setEditorClicked
      .pipe(takeUntil(this._onDestroy$))
      .subscribe((data) => {
        this.rightClickEventDetails = data;
      });
  }

  onOpenContextMenu(event: MouseEvent) {
    const linkContextMenu = this.citationService.contextMenuLength;
    if (event) {
      const length = linkContextMenu;
      const body = this.document.body,
        html = this.document.documentElement;

      const height = Math.max(
        body.scrollHeight,
        body.offsetHeight,
        html.clientHeight,
        html.scrollHeight,
        html.offsetHeight
      );
      const midHeight = (3 * height) / 4;
      this.userClickedHeight =
        event.pageY <= midHeight
          ? event.pageY * 0.85
          : length > 1
          ? event.pageY - this.largeOffset
          : event.pageY - this.smallOffset;
      this.globalTop = this.userClickedHeight;
      this.globalLeft = event.pageX;
      this.flagForCoords = true;
    }
  }

  getCoords() {
    switch (this.rightClickEventDetails) {
      case CommonConstants.editor.EDITOR:
        return {
          top: this.flagForCoords
            ? this.globalTop + 'px'
            : this.citationService.miniEditorCoords.top + 'px',
          left: this.flagForCoords
            ? this.globalLeft + 'px'
            : this.citationService.miniEditorCoords.left + 'px',
          position: 'absolute',
        };
      case CommonConstants.editor.RIGHT_HAND_EDITOR:
        return {
          top: `${this.wkDecoratorActionsService.contextMenuCoords.top + 30}px`,
          left: `${this.wkDecoratorActionsService.contextMenuCoords.left}px`,
          position: 'absolute',
        };
      default:
        return {
          top: `${this.citationService.miniEditorCoords.top + 30}px`,
          left: `${this.citationService.miniEditorCoords.left}px`,
          position: 'absolute',
        };
    }
  }

  @HostListener('document:mousedown', ['$event'])
  clickout(event) {
    if (!this.eRef.nativeElement.contains(event.target)) {
      this.contextMenuObject = [];
      this.showSubMenu = false;
    }
  }

  @HostListener('document:contextmenu', ['$event'])
  onClickContextMenu(event) {
    this.onOpenContextMenu(event);
  }

  runAction(
    actionName: string,
    subMenuItem: string,
    actDetails: any,
    menu: ContextMenuInterface
  ) {
    this.showSubMenu = false;
    const removeSubTemplateRange =
      menu?.name === CommonConstants.removeSubtemplate &&
      menu?.deletableSubTempRange;
    const removeBulletListRange =
      (actionName === CommonConstants.CONTEXT_MENU_ITEMS_FOR_BULLETS.copy ||
        actionName === CommonConstants.CONTEXT_MENU_ITEMS_FOR_BULLETS.cut) &&
      menu?.bulletListRange;
    const eventDetails = {
      eventName: actionName,
      subMenuItemClicked: subMenuItem,
      actionDetails: actDetails,
      deletableSubTempRange:
        removeSubTemplateRange || removeBulletListRange || null,
      eventKey: menu.key,
    };
    this.contextmenuService.setEventDetails(eventDetails);
    this.contextMenuObject = [];
    this.contextmenuService.contextMenuClose.emit(true);
    if (actionName === 'Generate AI-UMC') {
      this.store.dispatch(setProcessingStateOnUMCAPI());
      // If Save button is enabled, trigger save first and generates UMC post doc save
      if (
        this.citationService.ckeditorInstanceFromEditorComp?.commands?.get?.(
          'save'
        ).isEnabled
      ) {
        this.citationService.changeSidePanelTabTo(SidePanelTabs.Comment);
        this.store.dispatch(setSelectedCommentsTabIndex({ index: 2 }));
        this.store.dispatch(
          setTriggerSaveApiInDoc({
            triggerSaveApiInDoc: true,
            payloadForUMCPostDocSave: {
              nodeId: this.citationService.nodeId,
              sectionId: menu.sectionIdForUMC,
            },
          })
        );
      } else {
        this.store.dispatch(
          loadAIUMCReleaseNotes({
            nodeId: this.citationService.nodeId,
            sectionId: menu.sectionIdForUMC,
          })
        );
      }
    }
  }

  ngOnDestroy() {
    this._onDestroy$.next();
    this._onDestroy$.complete();
  }
}
